import { useState, useMemo, useCallback } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import { useLocation } from "wouter";
import {
  Shield, FileOutput, CheckCircle2, XCircle, AlertTriangle,
  ArrowRight, ArrowLeft, Hash, Clock, Layers, Target,
  ChevronDown, ChevronRight, Copy, Download, Zap, Scale,
  FileCheck, Activity, BarChart3, Network, Lock, Unlock,
  ShieldCheck, Send, Play, Eye, Ban, ListChecks, Loader2,
  ShieldAlert, ShieldOff, Fingerprint, FileText, Mail
} from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { toast } from "sonner";
import { safeText, safeNumber, safeArray } from "@/lib/data-guard";
import { generateFOIARequest, generateCaseReport, generateEscalationLetter, openDocumentForPrint } from "@/lib/documentTemplates";

// ─── Types (Canonical — mirrors engine output exactly) ─────────────────────

/** case-export-engine.ts → TimelineEvent */
interface TimelineEvent {
  timestamp: string;
  event_type: "created" | "evidence_added" | "finding_added" | "action_added" | "status_change" | "feedback" | "coordination" | "export";
  description: string;
  source: string;
  actor: string | null;
}

/** case-export-engine.ts → FactHash */
interface FactHash {
  algorithm: "sha256";
  hash: string;
  input_fields: string[];
  computed_at: string;
}

/** case-export-engine.ts → CoverageScore */
interface CoverageScore {
  total_score: number;
  max_possible: number;
  percentage: number;
  confidence: "high" | "medium" | "low";
  breakdown: Array<{
    field: string;
    weight: number;
    present: boolean;
    quality: string;
    score: number;
  }>;
  missing_fields: string[];
  overall: number;
  evidence_coverage: number;
  finding_coverage: number;
  action_coverage: number;
  coordination_coverage: number;
  friction_coverage: number;
}

/** case-export-engine.ts → TraceabilityBlock (singular object, NOT array) */
interface TraceabilityBlock {
  export_id: string;
  case_id: string;
  fact_hash: FactHash;
  schema_version: string;
  exported_at: string;
  source_system: "luminari";
  validation: any;
  coverage: CoverageScore;
  self_sufficient: boolean;
  reproducibility_guarantee: string;
}

/** case-export-engine.ts → RelationshipBlock */
interface RelationshipBlock {
  correlated_instances: Array<{
    record_id: string;
    problem_type: string;
    jurisdiction: string;
    correlation_score: number;
    match_type: string;
  }>;
  evidence_links: Array<{
    from_finding: string;
    to_evidence: string;
    link_type: string;
    confidence: number;
  }>;
  cross_system_connections: Array<{
    system_a: string;
    system_b: string;
    link_type: string;
    jurisdiction: string;
  }>;
}

/** case-export-engine.ts → CaseExportPayload */
interface CaseExportPayload {
  schema_version: "3.0";
  export_type: "case_export";
  source_system: "luminari";
  exported_at: string;
  core_state: any; // ValidatedInstance — opaque to frontend
  source_facts: {
    evidence_count: number;
    finding_count: number;
    action_count: number;
    evidence_items: any[];
    finding_items: any[];
    action_items: any[];
  };
  timeline: TimelineEvent[];
  derived_context: {
    friction_narrative: string;
    alignment_narrative: string;
    risk_narrative: string;
    coordination_narrative: string;
    jurisdiction_narrative: string;
    recommended_pathway_narrative: string;
    dominant_problem_type: string;
    dominant_jurisdiction: string;
    dominant_system: string;
    avg_friction: number;
    max_friction: number;
    coordination_summary: {
      deadlocked: number;
      with_conflicts: number;
      total_systems: number;
    };
  };
  traceability: TraceabilityBlock;
  coverage: CoverageScore;
  relationships: RelationshipBlock;
}

/** action-transmission-engine.ts → EscalationStep */
interface EscalationStep {
  level: number;
  trigger: string;
  action: string;
  target: string;
  timeline_days: number;
}

/** action-transmission-engine.ts → ActionItem */
interface ActionItem {
  id: string;
  action_type: string;
  description: string;
  target_entity: string;
  target_system: string;
  jurisdiction: string;
  priority: number;
  urgency: string;
  status: string;
  estimated_timeline_days: number;
  success_probability: number;
  cascade_impact: string;
  evidence_refs: string[];
  finding_refs: string[];
  dedup_hash: string;
  escalation_path: EscalationStep[];
}

/** action-transmission-engine.ts → TransmissionEndpoint */
interface TransmissionEndpoint {
  entity_name: string;
  entity_type: string;
  jurisdiction: string;
  system: string;
  contact_method: string;
  validation_status: "validated" | "inferred" | "unverified";
}

/** action-transmission-engine.ts → ActionBundle */
interface ActionBundle {
  bundle_id: string;
  case_id: string;
  export_id: string;
  fact_hash: string;
  schema_version: string;
  generated_at: string;
  summary: {
    total_actions: number;
    by_priority: Record<string, number>;
    by_urgency: Record<string, number>;
    by_status: Record<string, number>;
    estimated_total_timeline_days: number;
    avg_success_probability: number;
  };
  actions: ActionItem[];
  endpoints: TransmissionEndpoint[];
  escalation_matrix: {
    levels: number;
    triggers: string[];
    max_escalation_timeline_days: number;
  };
  bundle_hash: string;
  source_export_hash: string;
  dedup_count: number;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatTimestamp(ts: string | number | undefined | null): string {
  if (!ts) return "—";
  const d = typeof ts === "number" ? new Date(ts) : new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function truncateHash(hash: string, len = 12): string {
  if (!hash) return "—";
  return hash.length > len ? `${hash.slice(0, len)}...` : hash;
}

function coverageColor(score: number): string {
  if (score >= 0.8) return "text-green-400";
  if (score >= 0.6) return "text-yellow-400";
  if (score >= 0.4) return "text-orange-400";
  return "text-red-400";
}

function coverageBg(score: number): string {
  if (score >= 0.8) return "bg-green-500";
  if (score >= 0.6) return "bg-yellow-500";
  if (score >= 0.4) return "bg-orange-500";
  return "bg-red-500";
}

function priorityLabel(p: number): string {
  if (p >= 8) return "CRITICAL";
  if (p >= 6) return "HIGH";
  if (p >= 4) return "MEDIUM";
  return "LOW";
}

function priorityColor(p: number): string {
  if (p >= 8) return "text-red-400 bg-red-500/10 border-red-500/20";
  if (p >= 6) return "text-orange-400 bg-orange-500/10 border-orange-500/20";
  if (p >= 4) return "text-yellow-400 bg-yellow-500/10 border-yellow-500/20";
  return "text-green-400 bg-green-500/10 border-green-500/20";
}

// ─── Coverage Bar ───────────────────────────────────────────────────────────

function CoverageBar({ label, value }: { label: string; value: number }) {
  const pct = Math.round(value * 100);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-muted-foreground uppercase">{label}</span>
        <span className={`text-xs font-bold ${coverageColor(value)}`}>{pct}%</span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-muted">
        <div className={`h-1.5 rounded-full ${coverageBg(value)} transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ─── Fact Hash Verification Badge ───────────────────────────────────────────

function FactHashBadge({ hash, verified }: { hash: string; verified?: boolean }) {
  return (
    <div className="flex items-center gap-2">
      <Hash className="h-3.5 w-3.5 text-muted-foreground" />
      <code className="text-xs font-mono text-muted-foreground">{truncateHash(hash, 16)}</code>
      {verified === true && (
        <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] px-1.5">
          <CheckCircle2 className="h-3 w-3 mr-1" /> VERIFIED
        </Badge>
      )}
      {verified === false && (
        <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px] px-1.5">
          <XCircle className="h-3 w-3 mr-1" /> MISMATCH
        </Badge>
      )}
      <button
        className="text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => navigator.clipboard.writeText(hash)}
        title="Copy full hash"
      >
        <Copy className="h-3 w-3" />
      </button>
    </div>
  );
}

// ─── Timeline Event Row ─────────────────────────────────────────────────────

function TimelineEventRow({ event, index }: { event: TimelineEvent; index: number }) {
  const typeColors: Record<string, string> = {
    created: "text-cyan-400 bg-cyan-500/10",
    evidence_added: "text-blue-400 bg-blue-500/10",
    finding_added: "text-purple-400 bg-purple-500/10",
    action_added: "text-green-400 bg-green-500/10",
    status_change: "text-orange-400 bg-orange-500/10",
    feedback: "text-pink-400 bg-pink-500/10",
    coordination: "text-yellow-400 bg-yellow-500/10",
    export: "text-teal-400 bg-teal-500/10",
  };

  const colorClass = typeColors[event.event_type] || "text-muted-foreground bg-muted/30";

  return (
    <div className="flex items-start gap-3 relative">
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        <div className={`h-2.5 w-2.5 rounded-full ${colorClass.split(" ")[1]} border-2 border-background mt-1.5`} />
        <div className="w-px flex-1 bg-border/30 mt-1" />
      </div>
      <div className="flex-1 pb-4">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${colorClass}`}>
            {event.event_type.replace(/_/g, " ")}
          </span>
          <span className="text-[10px] text-muted-foreground font-mono">{formatTimestamp(event.timestamp)}</span>
        </div>
        <p className="text-xs text-foreground">{event.description}</p>
        <p className="text-[10px] text-muted-foreground font-mono mt-0.5">
          source: {event.source}{event.actor ? ` — actor: ${event.actor}` : ""}
        </p>
      </div>
    </div>
  );
}

// ─── Action Item Card ───────────────────────────────────────────────────────

function ActionItemCard({ action, bundle }: { action: ActionItem; bundle: ActionBundle }) {
  const [expanded, setExpanded] = useState(false);

  // Derive endpoint validation for this action from bundle.endpoints
  const matchingEndpoint = bundle.endpoints.find(
    (ep) => ep.system === action.target_system && ep.jurisdiction === action.jurisdiction
  );
  const endpointValid = matchingEndpoint?.validation_status === "validated";

  return (
    <div className="border border-border/30 rounded-lg overflow-hidden">
      <button
        className="w-full flex items-center gap-3 p-3 hover:bg-muted/20 transition-colors text-left"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Badge className={`text-[10px] ${priorityColor(action.priority)}`}>
              P{action.priority} — {priorityLabel(action.priority)}
            </Badge>
            <span className="text-xs font-mono text-muted-foreground">{action.action_type}</span>
          </div>
          <p className="text-xs text-foreground mt-1 truncate">{action.description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {endpointValid ? (
            <CheckCircle2 className="h-4 w-4 text-green-400" />
          ) : (
            <XCircle className="h-4 w-4 text-red-400" />
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-border/20 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Target Entity</p>
              <p className="text-xs text-foreground mt-0.5">{action.target_entity}</p>
            </div>
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Dedup Hash</p>
              <code className="text-[10px] font-mono text-muted-foreground">{truncateHash(action.dedup_hash, 16)}</code>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Target System</p>
              <p className="text-xs text-foreground mt-0.5">{action.target_system}</p>
            </div>
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Jurisdiction</p>
              <p className="text-xs text-foreground mt-0.5">{action.jurisdiction}</p>
            </div>
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Status</p>
              <p className="text-xs text-foreground mt-0.5">{action.status}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Urgency</p>
              <p className="text-xs text-foreground mt-0.5">{action.urgency}</p>
            </div>
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Timeline</p>
              <p className="text-xs text-foreground mt-0.5">{action.estimated_timeline_days}d</p>
            </div>
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase">Success Prob.</p>
              <p className="text-xs text-foreground mt-0.5">{Math.round(action.success_probability * 100)}%</p>
            </div>
          </div>

          {/* Endpoint Validation (derived from bundle.endpoints) */}
          <div className={`p-2 rounded border ${endpointValid ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"}`}>
            <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Endpoint Validation</p>
            <div className="flex items-center gap-2">
              {endpointValid ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-green-400" />
              ) : (
                <XCircle className="h-3.5 w-3.5 text-red-400" />
              )}
              <span className="text-xs text-foreground">
                {matchingEndpoint
                  ? `${matchingEndpoint.entity_name} — ${matchingEndpoint.validation_status} (${matchingEndpoint.contact_method})`
                  : "No matching endpoint found"}
              </span>
            </div>
          </div>

          {/* Cascade Impact */}
          {action.cascade_impact && (
            <div className="p-2 rounded bg-muted/20 border border-border/20">
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Cascade Impact</p>
              <p className="text-xs text-foreground">{action.cascade_impact}</p>
            </div>
          )}

          {/* Escalation Path */}
          {action.escalation_path && action.escalation_path.length > 0 && (
            <div>
              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Escalation Path</p>
              <div className="space-y-1">
                {action.escalation_path.map((step, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <Badge variant="outline" className="text-[10px] shrink-0">L{step.level}</Badge>
                    <span className="text-foreground">{step.target}</span>
                    <span className="text-muted-foreground">— {step.trigger}</span>
                    <span className="text-muted-foreground ml-auto shrink-0">{step.timeline_days}d</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Evidence & Finding Refs */}
          {(action.evidence_refs.length > 0 || action.finding_refs.length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              {action.evidence_refs.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Evidence Refs</p>
                  <div className="flex flex-wrap gap-1">
                    {action.evidence_refs.map((id) => (
                      <code key={id} className="text-[10px] font-mono text-blue-400 bg-blue-500/10 px-1.5 py-0.5 rounded">{truncateHash(id, 8)}</code>
                    ))}
                  </div>
                </div>
              )}
              {action.finding_refs.length > 0 && (
                <div>
                  <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Finding Refs</p>
                  <div className="flex flex-wrap gap-1">
                    {action.finding_refs.map((id) => (
                      <code key={id} className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded">{truncateHash(id, 8)}</code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Case Selector ──────────────────────────────────────────────────────────

function CaseSelector({ selectedId, onSelect }: { selectedId: string; onSelect: (id: string) => void }) {
  const { data: problemsData } = trpc.problems.list.useQuery({ limit: 100, offset: 0 });
  const instances = safeArray(problemsData?.instances);

  // Group by jurisdiction
  const grouped = useMemo(() => {
    const map: Record<string, typeof instances> = {};
    for (const inst of instances) {
      const j = safeText((inst as any).jurisdiction, "Unknown");
      if (!map[j]) map[j] = [];
      map[j].push(inst);
    }
    return Object.entries(map).sort(([a], [b]) => a.localeCompare(b));
  }, [instances]);

  return (
    <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
      {grouped.map(([jurisdiction, items]) => (
        <div key={jurisdiction}>
          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider mb-1 px-1">{jurisdiction}</p>
          <div className="space-y-0.5">
            {items.map((inst: any) => (
              <button
                key={(inst as any).id}
                className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                  selectedId === (inst as any).id
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "hover:bg-muted/30 text-foreground"
                }`}
                onClick={() => onSelect((inst as any).id)}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono">{safeText((inst as any).record_id, (inst as any).id)}</span>
                  <Badge variant="outline" className="text-[9px] ml-2">{safeText((inst as any).problem_type)}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Approval State Helpers ─────────────────────────────────────────────────

type ApprovalState = "not_ready" | "ready_for_review" | "prepared" | "approved" | "sent" | "acknowledged" | "completed";

const STATE_LABELS: Record<ApprovalState, string> = {
  not_ready: "NOT READY",
  ready_for_review: "READY FOR REVIEW",
  prepared: "PREPARED",
  approved: "APPROVED",
  sent: "SENT",
  acknowledged: "ACKNOWLEDGED",
  completed: "COMPLETED",
};

const STATE_COLORS: Record<ApprovalState, string> = {
  not_ready: "text-red-400 bg-red-500/10 border-red-500/20",
  ready_for_review: "text-yellow-400 bg-yellow-500/10 border-yellow-500/20",
  prepared: "text-blue-400 bg-blue-500/10 border-blue-500/20",
  approved: "text-green-400 bg-green-500/10 border-green-500/20",
  sent: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20",
  acknowledged: "text-purple-400 bg-purple-500/10 border-purple-500/20",
  completed: "text-emerald-400 bg-emerald-500/10 border-emerald-500/20",
};

const STATE_ORDER: ApprovalState[] = ["not_ready", "ready_for_review", "prepared", "approved", "sent", "acknowledged", "completed"];

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ExportActions() {
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [selectedCaseId, setSelectedCaseId] = useState<string>("");
  const [activeTab, setActiveTab] = useState<"export" | "actions" | "approval">("export");
  const [showTimeline, setShowTimeline] = useState(false);
  const [showTraceability, setShowTraceability] = useState(false);
  const [showRelationships, setShowRelationships] = useState(false);

  // Fetch export data when a case is selected
  const { data: exportData, isLoading: loadingExport, error: exportError } = trpc.caseExport.generate.useQuery(
    { caseId: selectedCaseId },
    { enabled: !!selectedCaseId }
  );

  // Fetch action bundle when a case is selected
  const { data: actionData, isLoading: loadingActions, error: actionError } = trpc.actionBundle.generate.useQuery(
    { caseId: selectedCaseId },
    { enabled: !!selectedCaseId }
  );

  // Verify fact hash — use traceability.fact_hash.hash from canonical structure
  const factHashValue = (exportData as any)?.traceability?.fact_hash?.hash || "";
  const { data: verifyResult } = trpc.caseExport.verify.useQuery(
    { caseId: selectedCaseId, expectedHash: factHashValue },
    { enabled: !!selectedCaseId && !!factHashValue }
  );

  const exportPayload = exportData as CaseExportPayload | undefined;
  const actionBundle = actionData as ActionBundle | undefined;

  //  // ─── Truth Validation Gate (runs BEFORE readiness) ──────────────────
  const { data: truthData, isLoading: loadingTruth } = trpc.truthValidation.check.useQuery(
    { caseId: selectedCaseId },
    { enabled: !!selectedCaseId }
  );
  const truthValidated = truthData?.truth_validated ?? false;

  // ─── Readiness Gate (blocked if truth fails) ────────────────────────
  const { data: readinessData, isLoading: loadingReadiness } = trpc.readiness.check.useQuery(
    { caseId: selectedCaseId },
    { enabled: !!selectedCaseId }
  );

  // ─── Approval Queue State ──────────────────────────────────────────────
  const { data: approvalStateData } = trpc.approval.state.useQuery(
    { caseId: selectedCaseId },
    { enabled: !!selectedCaseId }
  );
  const currentApprovalState: ApprovalState = (approvalStateData?.state as ApprovalState) ?? (readinessData?.current_state as ApprovalState) ?? "not_ready";

  // ─── Approval Queue List (admin only) ─────────────────────────────────
  const { data: approvalQueueList, refetch: refetchQueue } = trpc.approval.list.useQuery(
    undefined,
    { enabled: isAdmin && activeTab === "approval" }
  );

  // ─── Mutations ─────────────────────────────────────────────────────────
  const utils = trpc.useUtils();

  const prepareMutation = trpc.readiness.prepare.useMutation({
    onSuccess: (data) => {
      toast.success(`Case prepared: ${data.action_count} actions, ${data.endpoint_count} endpoints`);
      utils.approval.state.invalidate({ caseId: selectedCaseId });
      utils.approval.list.invalidate();
    },
    onError: (err) => {
      toast.error(`Prepare failed: ${err.message}`);
    },
  });

  const approveMutation = trpc.approval.approve.useMutation({
    onSuccess: () => {
      toast.success("Case approved");
      utils.approval.state.invalidate();
      utils.approval.list.invalidate();
    },
    onError: (err) => toast.error(`Approve failed: ${err.message}`),
  });

  const queueMutation = trpc.approval.queueForTransmission.useMutation({
    onSuccess: (data: { success: boolean; case_id: string; state: string; queued_at: string | null; guard_failures: string[] }) => {
      if (data.success) {
        toast.success(`Queued for transmission — state: ${data.state}`);
      } else {
        toast.error(`Queue blocked by guards: ${(data.guard_failures ?? []).join("; ")}`);
      }
      utils.approval.state.invalidate();
      utils.approval.list.invalidate();
    },
    onError: (err: any) => toast.error(`Queue failed: ${err.message}`),
  });

  const sendMutation = trpc.approval.send.useMutation({
    onSuccess: (data: { success: boolean; case_id: string; state: string; sent_at: string | null; receipt_id: string | null; guard_failures: string[] }) => {
      if (data.success) {
        toast.success(`Sent — receipt: ${data.receipt_id ?? "pending"}`);
      } else {
        toast.error(`Send blocked by guards: ${(data.guard_failures ?? []).join("; ")}`);
      }
      utils.approval.state.invalidate();
      utils.approval.list.invalidate();
    },
    onError: (err: any) => toast.error(`Send failed: ${err.message}`),
  });

  const acknowledgeMutation = trpc.approval.acknowledge.useMutation({
    onSuccess: () => {
      toast.success("Transmission acknowledged");
      utils.approval.state.invalidate();
      utils.approval.list.invalidate();
    },
    onError: (err) => toast.error(`Acknowledge failed: ${err.message}`),
  });

  const completeMutation = trpc.approval.complete.useMutation({
    onSuccess: () => {
      toast.success("Case completed");
      utils.approval.state.invalidate();
      utils.approval.list.invalidate();
    },
    onError: (err) => toast.error(`Complete failed: ${err.message}`),
  });

  const handlePrepare = useCallback(() => {
    if (!selectedCaseId) return;
    prepareMutation.mutate({ caseId: selectedCaseId });
  }, [selectedCaseId, prepareMutation]);

  const handleApprove = useCallback((caseId: string) => {
    approveMutation.mutate({ caseId });
  }, [approveMutation]);

  const handleQueue = useCallback((caseId: string) => {
    queueMutation.mutate({ caseId });
  }, [queueMutation]);

  const handleSend = useCallback((caseId: string) => {
    sendMutation.mutate({ caseId });
  }, [sendMutation]);

  const handleAcknowledge = useCallback((caseId: string) => {
    acknowledgeMutation.mutate({ caseId });
  }, [acknowledgeMutation]);

  const handleComplete = useCallback((caseId: string) => {
    completeMutation.mutate({ caseId });
  }, [completeMutation]);

  // ─── Derived summaries from canonical structures ───────────────────────

  // Export: derive export_id, fact_hash, generated_at from traceability block
  const exportId = exportPayload?.traceability?.export_id ?? "";
  const exportFactHash = exportPayload?.traceability?.fact_hash?.hash ?? "";
  const exportGeneratedAt = exportPayload?.exported_at ?? "";

  // Export: derive instance count from source_facts
  const instanceCount = 1; // single-instance export by design

  // Export: derive jurisdiction/system summary from core_state
  const jurisdictionSummary = useMemo(() => {
    if (!exportPayload?.core_state) return {};
    const j = exportPayload.core_state.jurisdiction;
    return j ? { [j]: 1 } : {};
  }, [exportPayload]);

  const systemSummary = useMemo(() => {
    if (!exportPayload?.core_state) return {};
    const s = exportPayload.core_state.system_primary;
    return s ? { [s]: 1 } : {};
  }, [exportPayload]);

  // Action Bundle: derive escalation summary from escalation_matrix
  const escalationSummary = useMemo(() => {
    if (!actionBundle) return { total_escalation_paths: 0, max_escalation_depth: 0 };
    const totalPaths = actionBundle.actions.reduce((sum, a) => sum + (a.escalation_path?.length > 0 ? 1 : 0), 0);
    return {
      total_escalation_paths: totalPaths,
      max_escalation_depth: actionBundle.escalation_matrix?.levels ?? 0,
    };
  }, [actionBundle]);

  // Action Bundle: derive endpoint validation summary from endpoints[]
  const endpointValidationSummary = useMemo(() => {
    if (!actionBundle?.endpoints) return { total_validated: 0, valid_count: 0, validation_rate: 0 };
    const total = actionBundle.endpoints.length;
    const valid = actionBundle.endpoints.filter((ep) => ep.validation_status === "validated").length;
    return {
      total_validated: total,
      valid_count: valid,
      validation_rate: total > 0 ? valid / total : 0,
    };
  }, [actionBundle]);

  const handleDownloadExport = () => {
    if (!exportPayload) return;
    const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `case-export-${exportPayload.traceability?.case_id || selectedCaseId}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadBundle = () => {
    if (!actionBundle) return;
    const blob = new Blob([JSON.stringify(actionBundle, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `action-bundle-${actionBundle.case_id}-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/")}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Home
              </Button>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 border border-primary/20">
                <FileOutput className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight text-foreground">Export & Actions</h1>
                <p className="text-xs font-mono text-muted-foreground tracking-wider">PHASE 11 + 12 — OUTPUT / TRANSMISSION</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 rounded-md border border-green-500/20 bg-green-500/5 px-3 py-1.5">
                <Lock className="h-3 w-3 text-green-400" />
                <span className="text-[10px] font-mono text-green-400">SCHEMA PRESERVED</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        <div className="grid grid-cols-12 gap-6">
          {/* Left: Case Selector */}
          <div className="col-span-3">
            <Card className="p-4 bg-card border-border/50">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Layers className="h-4 w-4 text-primary" />
                Select Case
              </h3>
              <CaseSelector selectedId={selectedCaseId} onSelect={setSelectedCaseId} />
            </Card>
          </div>

          {/* Right: Export / Action Content */}
          <div className="col-span-9 space-y-4">
            {/* ═══ STICKY CONTEXT HEADER ═══ */}
            {selectedCaseId && activeTab !== "approval" && (() => {
              const caseRecord = exportPayload?.core_state;
              const recordId = caseRecord?.record_id || exportPayload?.traceability?.case_id || selectedCaseId;
              const problemType = caseRecord?.problem_type || "";
              const jurisdiction = caseRecord?.jurisdiction || "";
              const riskLevel = caseRecord?.risk_level || caseRecord?.severity || "";
              return (
                <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border/40 -mx-0 px-0 pb-2 mb-1">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <Hash className="h-3.5 w-3.5 text-primary" />
                        <span className="text-sm font-mono font-semibold text-foreground">{safeText(recordId)}</span>
                      </div>
                      {problemType && (
                        <Badge variant="outline" className="text-[10px] font-mono">{safeText(problemType)}</Badge>
                      )}
                      {jurisdiction && (
                        <Badge variant="outline" className="text-[10px] font-mono text-muted-foreground">
                          <Scale className="h-3 w-3 mr-1" />{safeText(jurisdiction)}
                        </Badge>
                      )}
                      {riskLevel && (
                        <Badge className={`text-[10px] ${
                          riskLevel === "critical" || riskLevel === "CRITICAL" ? "text-red-400 bg-red-500/10 border-red-500/20" :
                          riskLevel === "high" || riskLevel === "HIGH" ? "text-orange-400 bg-orange-500/10 border-orange-500/20" :
                          riskLevel === "medium" || riskLevel === "MEDIUM" ? "text-yellow-400 bg-yellow-500/10 border-yellow-500/20" :
                          "text-green-400 bg-green-500/10 border-green-500/20"
                        }`}>{safeText(riskLevel).toUpperCase()}</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[10px] ${STATE_COLORS[currentApprovalState]}`}>
                        {STATE_LABELS[currentApprovalState]}
                      </Badge>
                      {truthValidated && (
                        <Badge className="text-[10px] text-green-400 bg-green-500/10 border-green-500/20">
                          <Fingerprint className="h-3 w-3 mr-1" /> VERIFIED
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Tab Switcher */}
            <div className="flex items-center gap-2">
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "export"
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/20"
                }`}
                onClick={() => setActiveTab("export")}
              >
                <FileCheck className="h-4 w-4 inline mr-2" />
                Case Export
              </button>
              <button
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === "actions"
                    ? "bg-primary/10 text-primary border border-primary/20"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/20"
                }`}
                onClick={() => setActiveTab("actions")}
              >
                <Target className="h-4 w-4 inline mr-2" />
                Action Bundle
              </button>
              {isAdmin && (
                <button
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === "approval"
                      ? "bg-primary/10 text-primary border border-primary/20"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/20"
                  }`}
                  onClick={() => setActiveTab("approval")}
                >
                  <ShieldCheck className="h-4 w-4 inline mr-2" />
                  Approval Queue
                </button>
              )}
            </div>

            {/* ═══════════════════════════════════════════════════════════════
                TRUTH VALIDATION GATE (shown when case selected, before readiness)
            ═══════════════════════════════════════════════════════════════ */}
            {selectedCaseId && activeTab !== "approval" && (
              <Card className={`p-4 border-border/50 ${
                loadingTruth ? "bg-card" : truthValidated ? "bg-card" : "bg-red-950/20 border-red-500/30"
              }`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Fingerprint className="h-4 w-4 text-primary" />
                    Truth Validation Gate
                  </h3>
                  <div className="flex items-center gap-2">
                    {loadingTruth && <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />}
                    {!loadingTruth && truthData && (
                      <Badge className={`text-[10px] ${
                        truthValidated
                          ? "text-green-400 bg-green-500/10 border-green-500/20"
                          : "text-red-400 bg-red-500/10 border-red-500/20"
                      }`}>
                        {truthValidated ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> TRUTH VERIFIED</>
                        ) : (
                          <><ShieldOff className="h-3 w-3 mr-1" /> TRUTH FAILED</>
                        )}
                      </Badge>
                    )}
                  </div>
                </div>

                {/* 5-Check Grid */}
                {truthData && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-5 gap-2">
                      {([
                        { key: "traceability" as const, label: "Traceability", icon: Network },
                        { key: "evidence" as const, label: "Evidence", icon: FileCheck },
                        { key: "fact_completeness" as const, label: "Completeness", icon: ListChecks },
                        { key: "determinism" as const, label: "Determinism", icon: Fingerprint },
                        { key: "no_synthetic_data" as const, label: "No Synthetic", icon: ShieldAlert },
                      ]).map(({ key, label, icon: Icon }) => {
                        const check = truthData.checks[key];
                        const passed = check?.passed ?? false;
                        return (
                          <div key={key} className={`p-2 rounded border text-center ${
                            passed
                              ? "bg-green-500/5 border-green-500/20"
                              : "bg-red-500/5 border-red-500/20"
                          }`}>
                            <Icon className={`h-4 w-4 mx-auto mb-1 ${
                              passed ? "text-green-400" : "text-red-400"
                            }`} />
                            <p className="text-[9px] font-mono text-muted-foreground uppercase">{label}</p>
                            <p className={`text-[10px] font-bold ${
                              passed ? "text-green-400" : "text-red-400"
                            }`}>
                              {passed ? "PASS" : "FAIL"}
                            </p>
                            {check?.violations?.length > 0 && (
                              <p className="text-[9px] text-red-400 mt-0.5">{check.violations.length} violation(s)</p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Truth Fail Reasons */}
                    {!truthValidated && truthData.truth_fail_reasons.length > 0 && (
                      <div className="p-3 rounded bg-red-500/5 border border-red-500/20">
                        <p className="text-[10px] font-mono text-red-400 uppercase mb-1 flex items-center gap-1">
                          <ShieldOff className="h-3 w-3" /> BLOCKED — Truth Validation Failed
                        </p>
                        <p className="text-[10px] text-red-300 mb-2">No readiness, preparation, approval, or sending can proceed until all checks pass.</p>
                        <ul className="space-y-1">
                          {truthData.truth_fail_reasons.map((r: string, i: number) => (
                            <li key={i} className="text-xs text-red-300 flex items-start gap-1.5">
                              <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Per-Check Violations (expanded) */}
                    {!truthValidated && (
                      <div className="space-y-2">
                        {([
                          { key: "traceability" as const, label: "Traceability Violations" },
                          { key: "evidence" as const, label: "Evidence Violations" },
                          { key: "fact_completeness" as const, label: "Fact Completeness Violations" },
                          { key: "determinism" as const, label: "Determinism Violations" },
                          { key: "no_synthetic_data" as const, label: "Synthetic Data Violations" },
                        ]).map(({ key, label }) => {
                          const check = truthData.checks[key];
                          if (!check || check.passed || check.violations.length === 0) return null;
                          return (
                            <div key={key} className="p-2 rounded bg-red-500/5 border border-red-500/20">
                              <p className="text-[10px] font-mono text-red-400 uppercase mb-1">{label}</p>
                              <ul className="space-y-0.5">
                                {check.violations.map((v: string, i: number) => (
                                  <li key={i} className="text-[10px] text-red-300 font-mono">• {v}</li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Truth Validated Success */}
                    {truthValidated && (
                      <div className="p-2 rounded bg-green-500/5 border border-green-500/20">
                        <p className="text-[10px] text-green-400 flex items-center gap-1">
                          <CheckCircle2 className="h-3 w-3" />
                          All 5 truth checks passed. Data is verified, traceable, and grounded. Readiness gate may proceed.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                READINESS GATE + STATE FLOW (shown when case selected AND truth passes)
            ═══════════════════════════════════════════════════════════════ */}
            {selectedCaseId && activeTab !== "approval" && truthValidated && (
              <Card className="p-4 bg-card border-border/50">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                    Readiness Gate
                  </h3>
                  {loadingReadiness && <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />}
                  <Badge className={`text-[10px] ${STATE_COLORS[currentApprovalState]}`}>
                    {STATE_LABELS[currentApprovalState]}
                  </Badge>
                </div>

                {/* State Flow Visualization */}
                <div className="flex items-center gap-1 mb-3 overflow-x-auto pb-1">
                  {STATE_ORDER.map((s, i) => {
                    const isActive = s === currentApprovalState;
                    const isPast = STATE_ORDER.indexOf(currentApprovalState) > i;
                    return (
                      <div key={s} className="flex items-center gap-1">
                        <div className={`px-2 py-1 rounded text-[9px] font-mono whitespace-nowrap border ${
                          isActive ? STATE_COLORS[s] : isPast ? "text-muted-foreground bg-muted/30 border-border/30 line-through" : "text-muted-foreground/50 bg-muted/10 border-border/20"
                        }`}>
                          {STATE_LABELS[s]}
                        </div>
                        {i < STATE_ORDER.length - 1 && (
                          <ArrowRight className={`h-3 w-3 shrink-0 ${isPast ? "text-muted-foreground/50" : "text-muted-foreground/30"}`} />
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Readiness Metrics */}
                {readinessData && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-4 gap-3">
                      <div className={`p-2 rounded border ${
                        readinessData.readiness.metrics.evidence_count >= 3 ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"
                      }`}>
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Evidence</p>
                        <p className="text-lg font-bold text-foreground">{readinessData.readiness.metrics.evidence_count}</p>
                        <p className="text-[9px] text-muted-foreground">min: 3</p>
                      </div>
                      <div className={`p-2 rounded border ${
                        readinessData.readiness.metrics.high_confidence_signals >= 1 ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"
                      }`}>
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">High Conf. Signals</p>
                        <p className="text-lg font-bold text-foreground">{readinessData.readiness.metrics.high_confidence_signals}</p>
                        <p className="text-[9px] text-muted-foreground">min: 1 (conf &ge; 0.8)</p>
                      </div>
                      <div className={`p-2 rounded border ${
                        readinessData.readiness.metrics.coverage_score >= 0.7 ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"
                      }`}>
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Coverage</p>
                        <p className="text-lg font-bold text-foreground">{(readinessData.readiness.metrics.coverage_score * 100).toFixed(1)}%</p>
                        <p className="text-[9px] text-muted-foreground">min: 70%</p>
                      </div>
                      <div className={`p-2 rounded border ${
                        readinessData.readiness.metrics.missing_critical_fields.length === 0 ? "bg-green-500/5 border-green-500/20" : "bg-red-500/5 border-red-500/20"
                      }`}>
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Missing Fields</p>
                        <p className="text-lg font-bold text-foreground">{readinessData.readiness.metrics.missing_critical_fields.length}</p>
                        <p className="text-[9px] text-muted-foreground">must be 0</p>
                      </div>
                    </div>

                    {/* Not Ready: display reasons */}
                    {!readinessData.readiness.ready_for_review && readinessData.readiness.reasons.length > 0 && (
                      <div className="p-3 rounded bg-red-500/5 border border-red-500/20">
                        <p className="text-[10px] font-mono text-red-400 uppercase mb-1 flex items-center gap-1">
                          <Ban className="h-3 w-3" /> Not Ready — Reasons
                        </p>
                        <ul className="space-y-1">
                          {readinessData.readiness.reasons.map((r, i) => (
                            <li key={i} className="text-xs text-red-300 flex items-start gap-1.5">
                              <XCircle className="h-3 w-3 mt-0.5 shrink-0" />
                              {r}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Missing Critical Fields Detail */}
                    {readinessData.readiness.metrics.missing_critical_fields.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {readinessData.readiness.metrics.missing_critical_fields.map((f) => (
                          <code key={f} className="text-[10px] font-mono text-red-400 bg-red-500/10 px-1.5 py-0.5 rounded">{f}</code>
                        ))}
                      </div>
                    )}

                    {/* Ready for Review: show Prepare Action button */}
                    {readinessData.readiness.ready_for_review && currentApprovalState === "ready_for_review" && (
                      <div className="flex items-center gap-3 pt-1">
                        <Button
                          onClick={handlePrepare}
                          disabled={prepareMutation.isPending}
                          className="bg-primary text-primary-foreground"
                        >
                          {prepareMutation.isPending ? (
                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Preparing...</>
                          ) : (
                            <><Play className="h-4 w-4 mr-2" /> Prepare Action</>
                          )}
                        </Button>
                        <p className="text-[10px] text-muted-foreground">Generates export + action bundle and enqueues for admin approval. Does NOT send.</p>
                      </div>
                    )}

                    {/* Prepared / Approved / Sent / etc — show current state info */}
                    {(currentApprovalState === "prepared" || currentApprovalState === "approved" || currentApprovalState === "sent" || currentApprovalState === "acknowledged" || currentApprovalState === "completed") && (
                      <div className="p-3 rounded bg-muted/20 border border-border/20">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1 flex items-center gap-1">
                          <ListChecks className="h-3 w-3" /> Pipeline Status
                        </p>
                        <p className="text-xs text-foreground">
                          This case is <strong>{STATE_LABELS[currentApprovalState]}</strong>.
                          {currentApprovalState === "prepared" && " Awaiting admin approval."}
                          {currentApprovalState === "approved" && " Approved — awaiting transmission."}
                          {currentApprovalState === "sent" && " Transmitted — awaiting acknowledgment."}
                          {currentApprovalState === "acknowledged" && " Acknowledged — can be marked complete."}
                          {currentApprovalState === "completed" && " Transmission cycle complete."}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )}

            {!selectedCaseId && (
              <Card className="p-12 bg-card border-border/50 text-center">
                <Layers className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">Select a case from the left panel to generate export or action bundle.</p>
              </Card>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                CASE EXPORT TAB
            ═══════════════════════════════════════════════════════════════ */}
            {selectedCaseId && activeTab === "export" && (
              <div className="space-y-4">
                {loadingExport && (
                  <Card className="p-8 bg-card border-border/50 text-center">
                    <Activity className="h-8 w-8 text-primary mx-auto mb-3 animate-spin" />
                    <p className="text-sm text-muted-foreground">Generating case export...</p>
                  </Card>
                )}

                {exportError && (
                  <Card className="p-6 bg-card border-red-500/30">
                    <div className="flex items-center gap-3">
                      <XCircle className="h-5 w-5 text-red-400" />
                      <div>
                        <p className="text-sm font-medium text-red-400">Export Generation Failed</p>
                        <p className="text-xs text-muted-foreground mt-1">{(exportError as any)?.message || "Unknown error"}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {exportPayload && !loadingExport && (
                  <>
                    {/* Export Header — reads from traceability block */}
                    <Card className="p-4 bg-card border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <FileOutput className="h-4 w-4 text-primary" />
                            Export: {truncateHash(exportId, 20)}
                          </h3>
                          <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                            Generated {formatTimestamp(exportGeneratedAt)} — v{exportPayload.schema_version} — {exportPayload.source_system}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={handleDownloadExport}>
                            <Download className="h-3.5 w-3.5 mr-1.5" /> Download JSON
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => {
                            const mappedPayload = {
                              case_id: exportPayload.traceability.case_id,
                              record_id: exportPayload.core_state.record_id,
                              jurisdiction: exportPayload.core_state.jurisdiction,
                              system_primary: exportPayload.core_state.system_primary,
                              problem_type: exportPayload.derived_context.dominant_problem_type,
                              generated_at: exportPayload.exported_at,
                              schema_version: exportPayload.schema_version,
                              source_facts: exportPayload.source_facts,
                              derived_context: exportPayload.derived_context,
                              evidence: exportPayload.source_facts.evidence_items,
                              findings: exportPayload.source_facts.finding_items,
                              statutes: exportPayload.core_state.statutes,
                              remedy_paths: exportPayload.core_state.remedy_paths,
                              grounding_entities: exportPayload.core_state.grounding_entities,
                            };
                            const html = generateCaseReport(mappedPayload as any);
                            openDocumentForPrint(html, `Case-Report-${exportPayload.core_state.record_id}.html`);
                          }}>
                            <FileText className="h-3.5 w-3.5 mr-1.5" /> Generate Report
                          </Button>
                        </div>
                      </div>
                      <FactHashBadge hash={exportFactHash} verified={(verifyResult as any)?.verified ?? (verifyResult as any)?.matches} />
                    </Card>

                    {/* Source Facts Summary */}
                    <div className="grid grid-cols-3 gap-4">
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Evidence Items</p>
                        <p className="text-2xl font-bold text-foreground mt-1">{exportPayload.source_facts?.evidence_count ?? 0}</p>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Findings</p>
                        <p className="text-2xl font-bold text-foreground mt-1">{exportPayload.source_facts?.finding_count ?? 0}</p>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Actions Queued</p>
                        <p className="text-2xl font-bold text-foreground mt-1">{exportPayload.source_facts?.action_count ?? 0}</p>
                      </Card>
                    </div>

                    {/* Derived Context */}
                    <Card className="p-4 bg-card border-border/50">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">Derived Context</h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Dominant Problem</p>
                          <p className="text-sm font-semibold text-foreground mt-1">{exportPayload.derived_context?.dominant_problem_type ?? "—"}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Dominant Jurisdiction</p>
                          <p className="text-sm font-semibold text-foreground mt-1">{exportPayload.derived_context?.dominant_jurisdiction ?? "—"}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Dominant System</p>
                          <p className="text-sm font-semibold text-foreground mt-1">{exportPayload.derived_context?.dominant_system ?? "—"}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-3 mt-3">
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Avg Friction</p>
                          <p className="text-lg font-bold text-foreground">{((exportPayload.derived_context?.avg_friction ?? 0) * 100).toFixed(1)}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Max Friction</p>
                          <p className="text-lg font-bold text-foreground">{((exportPayload.derived_context?.max_friction ?? 0) * 100).toFixed(1)}%</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Deadlocked</p>
                          <p className="text-lg font-bold text-foreground">{exportPayload.derived_context?.coordination_summary?.deadlocked ?? 0}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">With Conflicts</p>
                          <p className="text-lg font-bold text-foreground">{exportPayload.derived_context?.coordination_summary?.with_conflicts ?? 0}</p>
                        </div>
                      </div>

                      {/* Narratives (collapsible) */}
                      {exportPayload.derived_context?.friction_narrative && (
                        <div className="mt-3 space-y-2">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase tracking-wider">Narratives</p>
                          <div className="p-2 rounded bg-muted/10 border border-border/20 text-xs text-muted-foreground leading-relaxed space-y-1">
                            <p>{exportPayload.derived_context.friction_narrative}</p>
                            <p>{exportPayload.derived_context.risk_narrative}</p>
                            <p>{exportPayload.derived_context.coordination_narrative}</p>
                          </div>
                        </div>
                      )}
                    </Card>

                    {/* Coverage Scoring */}
                    <Card className="p-4 bg-card border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider">Coverage Score</h4>
                        <div className="flex items-center gap-3">
                          <Badge variant="outline" className="text-[10px]">
                            {exportPayload.coverage?.confidence ?? "—"} confidence
                          </Badge>
                          <span className={`text-lg font-bold ${coverageColor(exportPayload.coverage?.overall ?? 0)}`}>
                            {Math.round((exportPayload.coverage?.overall ?? 0) * 100)}%
                          </span>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                        <CoverageBar label="Evidence" value={exportPayload.coverage?.evidence_coverage ?? 0} />
                        <CoverageBar label="Findings" value={exportPayload.coverage?.finding_coverage ?? 0} />
                        <CoverageBar label="Actions" value={exportPayload.coverage?.action_coverage ?? 0} />
                        <CoverageBar label="Coordination" value={exportPayload.coverage?.coordination_coverage ?? 0} />
                        <CoverageBar label="Friction" value={exportPayload.coverage?.friction_coverage ?? 0} />
                      </div>
                      {(exportPayload.coverage?.missing_fields?.length ?? 0) > 0 && (
                        <div className="mt-3 p-2 rounded bg-yellow-500/5 border border-yellow-500/20">
                          <p className="text-[10px] font-mono text-yellow-400 uppercase mb-1">Missing Fields</p>
                          <div className="flex flex-wrap gap-1">
                            {(exportPayload.coverage?.missing_fields ?? []).map((f) => (
                              <code key={f} className="text-[10px] font-mono text-yellow-400 bg-yellow-500/10 px-1.5 py-0.5 rounded">{f}</code>
                            ))}
                          </div>
                        </div>
                      )}
                    </Card>

                    {/* Jurisdiction & System Summary (derived from core_state) */}
                    <div className="grid grid-cols-2 gap-4">
                      <Card className="p-4 bg-card border-border/50">
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">Jurisdiction</h4>
                        <div className="space-y-1">
                          {Object.entries(jurisdictionSummary).map(([j, count]) => (
                            <div key={j} className="flex items-center justify-between text-xs">
                              <span className="text-foreground">{j}</span>
                              <Badge variant="outline" className="text-[10px]">{count as number}</Badge>
                            </div>
                          ))}
                        </div>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">System</h4>
                        <div className="space-y-1">
                          {Object.entries(systemSummary).map(([s, count]) => (
                            <div key={s} className="flex items-center justify-between text-xs">
                              <span className="text-foreground">{s}</span>
                              <Badge variant="outline" className="text-[10px]">{count as number}</Badge>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </div>

                    {/* Timeline (collapsible) */}
                    <Card className="p-4 bg-card border-border/50">
                      <button
                        className="w-full flex items-center justify-between"
                        onClick={() => setShowTimeline(!showTimeline)}
                      >
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                          <Clock className="h-3.5 w-3.5" />
                          Reconstructed Timeline ({(exportPayload.timeline ?? []).length} events)
                        </h4>
                        {showTimeline ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      {showTimeline && (
                        <div className="mt-3 max-h-[400px] overflow-y-auto">
                          {(exportPayload.timeline ?? []).map((event, i) => (
                            <TimelineEventRow key={`${event.event_type}-${i}`} event={event} index={i} />
                          ))}
                        </div>
                      )}
                    </Card>

                    {/* Traceability Block (singular object) */}
                    <Card className="p-4 bg-card border-border/50">
                      <button
                        className="w-full flex items-center justify-between"
                        onClick={() => setShowTraceability(!showTraceability)}
                      >
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                          <Layers className="h-3.5 w-3.5" />
                          Traceability Block
                        </h4>
                        {showTraceability ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      {showTraceability && exportPayload.traceability && (
                        <div className="mt-3 space-y-2 max-h-[400px] overflow-y-auto">
                          <div className="p-3 rounded bg-muted/30 border border-border/30 space-y-2">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">Export ID:</span>
                              <code className="text-xs font-mono text-foreground">{truncateHash(exportPayload.traceability.export_id, 24)}</code>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">Case ID:</span>
                              <code className="text-xs font-mono text-foreground">{truncateHash(exportPayload.traceability.case_id, 24)}</code>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">Schema:</span>
                              <span className="text-xs font-mono text-foreground">{exportPayload.traceability.schema_version}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">Exported At:</span>
                              <span className="text-xs font-mono text-foreground">{formatTimestamp(exportPayload.traceability.exported_at)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">Self-sufficient:</span>
                              <span className={`text-xs font-semibold ${exportPayload.traceability.self_sufficient ? "text-green-400" : "text-yellow-400"}`}>
                                {exportPayload.traceability.self_sufficient ? "Yes" : "No"}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-mono text-muted-foreground">Guarantee:</span>
                              <span className="text-xs text-muted-foreground">{exportPayload.traceability.reproducibility_guarantee}</span>
                            </div>
                            {exportPayload.traceability.fact_hash && (
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono text-muted-foreground">Fact Hash:</span>
                                <code className="text-xs font-mono text-foreground">{truncateHash(exportPayload.traceability.fact_hash.hash, 20)}</code>
                                <span className="text-[10px] text-muted-foreground">({exportPayload.traceability.fact_hash.algorithm})</span>
                              </div>
                            )}
                            {exportPayload.traceability.coverage && (
                              <div className="flex items-center gap-2">
                                <span className="text-[10px] font-mono text-muted-foreground">Coverage:</span>
                                <span className="text-xs font-mono text-foreground">{Math.round(exportPayload.traceability.coverage.percentage ?? 0)}%</span>
                                <Badge variant="outline" className="text-[10px]">{exportPayload.traceability.coverage.confidence}</Badge>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>

                    {/* Relationships Block */}
                    <Card className="p-4 bg-card border-border/50">
                      <button
                        className="w-full flex items-center justify-between"
                        onClick={() => setShowRelationships(!showRelationships)}
                      >
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                          <Network className="h-3.5 w-3.5" />
                          Relationships ({
                            (exportPayload.relationships?.correlated_instances?.length ?? 0) +
                            (exportPayload.relationships?.evidence_links?.length ?? 0) +
                            (exportPayload.relationships?.cross_system_connections?.length ?? 0)
                          } total)
                        </h4>
                        {showRelationships ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </button>
                      {showRelationships && exportPayload.relationships && (
                        <div className="mt-3 space-y-3 max-h-[400px] overflow-y-auto">
                          {/* Correlated Instances */}
                          {(exportPayload.relationships.correlated_instances?.length ?? 0) > 0 && (
                            <div>
                              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Correlated Instances ({exportPayload.relationships.correlated_instances.length})</p>
                              <div className="space-y-1">
                                {exportPayload.relationships.correlated_instances.map((ci, i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted/10">
                                    <code className="font-mono text-primary">{ci.record_id}</code>
                                    <Badge variant="outline" className="text-[9px]">{ci.match_type}</Badge>
                                    <span className="text-muted-foreground">{ci.jurisdiction}</span>
                                    <span className="ml-auto text-muted-foreground">{Math.round(ci.correlation_score * 100)}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Evidence Links */}
                          {(exportPayload.relationships.evidence_links?.length ?? 0) > 0 && (
                            <div>
                              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Evidence Links ({exportPayload.relationships.evidence_links.length})</p>
                              <div className="space-y-1">
                                {exportPayload.relationships.evidence_links.map((el, i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted/10">
                                    <code className="font-mono text-purple-400">{truncateHash(el.from_finding, 8)}</code>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <code className="font-mono text-blue-400">{truncateHash(el.to_evidence, 8)}</code>
                                    <Badge variant="outline" className="text-[9px]">{el.link_type}</Badge>
                                    <span className="ml-auto text-muted-foreground">{Math.round(el.confidence * 100)}%</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Cross-System Connections */}
                          {(exportPayload.relationships.cross_system_connections?.length ?? 0) > 0 && (
                            <div>
                              <p className="text-[10px] font-mono text-muted-foreground uppercase mb-1">Cross-System Connections ({exportPayload.relationships.cross_system_connections.length})</p>
                              <div className="space-y-1">
                                {exportPayload.relationships.cross_system_connections.map((cs, i) => (
                                  <div key={i} className="flex items-center gap-2 text-xs p-1.5 rounded bg-muted/10">
                                    <span className="text-foreground">{cs.system_a}</span>
                                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                                    <span className="text-foreground">{cs.system_b}</span>
                                    <Badge variant="outline" className="text-[9px]">{cs.link_type}</Badge>
                                    <span className="ml-auto text-muted-foreground">{cs.jurisdiction}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Empty state */}
                          {(exportPayload.relationships.correlated_instances?.length ?? 0) === 0 &&
                           (exportPayload.relationships.evidence_links?.length ?? 0) === 0 &&
                           (exportPayload.relationships.cross_system_connections?.length ?? 0) === 0 && (
                            <p className="text-xs text-muted-foreground text-center py-4">No relationships detected for this instance.</p>
                          )}
                        </div>
                      )}
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                ACTION BUNDLE TAB
            ═══════════════════════════════════════════════════════════════ */}
            {selectedCaseId && activeTab === "actions" && (
              <div className="space-y-4">
                {loadingActions && (
                  <Card className="p-8 bg-card border-border/50 text-center">
                    <Activity className="h-8 w-8 text-primary mx-auto mb-3 animate-spin" />
                    <p className="text-sm text-muted-foreground">Generating action bundle...</p>
                  </Card>
                )}

                {actionError && (
                  <Card className="p-6 bg-card border-red-500/30">
                    <div className="flex items-center gap-3">
                      <XCircle className="h-5 w-5 text-red-400" />
                      <div>
                        <p className="text-sm font-medium text-red-400">Action Bundle Generation Failed</p>
                        <p className="text-xs text-muted-foreground mt-1">{(actionError as any)?.message || "Unknown error"}</p>
                      </div>
                    </div>
                  </Card>
                )}

                {actionBundle && !loadingActions && (
                  <>
                    {/* Bundle Header — reads from canonical fields */}
                    <Card className="p-4 bg-card border-border/50">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                            <Target className="h-4 w-4 text-primary" />
                            Bundle: {truncateHash(actionBundle.bundle_id, 20)}
                          </h3>
                          <p className="text-[10px] font-mono text-muted-foreground mt-0.5">
                            Generated {formatTimestamp(actionBundle.generated_at)} — {actionBundle.summary.total_actions} actions ({actionBundle.dedup_count} deduplicated)
                          </p>
                        </div>
                        <Button variant="outline" size="sm" onClick={handleDownloadBundle}>
                          <Download className="h-3.5 w-3.5 mr-1.5" /> Download JSON
                        </Button>
                      </div>
                      <FactHashBadge hash={actionBundle.fact_hash} />
                    </Card>

                    {/* Bundle Summary — reads from summary.*, escalation_matrix, endpoints[] */}
                    <div className="grid grid-cols-4 gap-4">
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Total Actions</p>
                        <p className="text-2xl font-bold text-foreground mt-1">{actionBundle.summary.total_actions}</p>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Deduplicated</p>
                        <p className="text-2xl font-bold text-foreground mt-1">{actionBundle.dedup_count}</p>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Escalation Paths</p>
                        <p className="text-2xl font-bold text-foreground mt-1">{escalationSummary.total_escalation_paths}</p>
                        <p className="text-[10px] text-muted-foreground">max depth: {escalationSummary.max_escalation_depth}</p>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <p className="text-[10px] font-mono text-muted-foreground uppercase">Endpoint Validation</p>
                        <p className={`text-2xl font-bold ${endpointValidationSummary.validation_rate >= 0.8 ? "text-green-400" : endpointValidationSummary.validation_rate >= 0.5 ? "text-yellow-400" : "text-red-400"}`}>
                          {Math.round(endpointValidationSummary.validation_rate * 100)}%
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {endpointValidationSummary.valid_count}/{endpointValidationSummary.total_validated} valid
                        </p>
                      </Card>
                    </div>

                    {/* Priority / Urgency / Status Breakdown */}
                    <div className="grid grid-cols-3 gap-4">
                      <Card className="p-4 bg-card border-border/50">
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">By Priority</h4>
                        <div className="space-y-1">
                          {Object.entries(actionBundle.summary.by_priority ?? {}).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-xs">
                              <span className="text-foreground">P{k}</span>
                              <Badge variant="outline" className="text-[10px]">{v}</Badge>
                            </div>
                          ))}
                        </div>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">By Urgency</h4>
                        <div className="space-y-1">
                          {Object.entries(actionBundle.summary.by_urgency ?? {}).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-xs">
                              <span className="text-foreground">{k}</span>
                              <Badge variant="outline" className="text-[10px]">{v}</Badge>
                            </div>
                          ))}
                        </div>
                      </Card>
                      <Card className="p-4 bg-card border-border/50">
                        <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">By Status</h4>
                        <div className="space-y-1">
                          {Object.entries(actionBundle.summary.by_status ?? {}).map(([k, v]) => (
                            <div key={k} className="flex items-center justify-between text-xs">
                              <span className="text-foreground">{k}</span>
                              <Badge variant="outline" className="text-[10px]">{v}</Badge>
                            </div>
                          ))}
                        </div>
                      </Card>
                    </div>

                    {/* Transmission Endpoints */}
                    <Card className="p-4 bg-card border-border/50">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
                        Transmission Endpoints ({(actionBundle.endpoints ?? []).length})
                      </h4>
                      <div className="space-y-1.5">
                        {(actionBundle.endpoints ?? []).map((ep, i) => (
                          <div key={i} className="flex items-center gap-2 text-xs p-2 rounded bg-muted/10 border border-border/20">
                            <span className="text-foreground font-medium">{ep.entity_name}</span>
                            <Badge variant="outline" className="text-[9px]">{ep.entity_type}</Badge>
                            <span className="text-muted-foreground">{ep.jurisdiction} / {ep.system}</span>
                            <span className="text-muted-foreground ml-auto">{ep.contact_method}</span>
                            <Badge className={`text-[9px] ${
                              ep.validation_status === "validated"
                                ? "bg-green-500/10 text-green-400 border-green-500/20"
                                : ep.validation_status === "inferred"
                                ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/20"
                                : "bg-red-500/10 text-red-400 border-red-500/20"
                            }`}>{ep.validation_status}</Badge>
                          </div>
                        ))}
                      </div>
                      {exportPayload && (
                        <div className="mt-4 space-y-2">
                          {(actionBundle.endpoints ?? []).filter(ep => ep.entity_type === "government_agency").length > 0 && (
                            <Button variant="outline" size="sm" className="w-full" onClick={() => {
                              const caseData = {
                                record_id: exportPayload.core_state.record_id,
                                jurisdiction: exportPayload.core_state.jurisdiction,
                                system_primary: exportPayload.core_state.system_primary,
                                evidence: exportPayload.source_facts.evidence_items,
                                findings: exportPayload.source_facts.finding_items,
                                statutes: exportPayload.core_state.statutes,
                              };
                              const targetAgency = (actionBundle.endpoints ?? []).find(ep => ep.entity_type === "government_agency")?.entity_name || "Government Agency";
                              const html = generateFOIARequest(caseData as any, targetAgency);
                              openDocumentForPrint(html, `FOIA-Request-${exportPayload.core_state.record_id}.html`);
                            }}>
                              <Mail className="h-3.5 w-3.5 mr-1.5" /> Generate FOIA Request
                            </Button>
                          )}
                          {(actionBundle.endpoints ?? []).filter(ep => ep.entity_type === "nonprofit_advocacy" || ep.entity_type === "legal_aid_foundation").length > 0 && (
                            <Button variant="outline" size="sm" className="w-full" onClick={() => {
                              const caseData = {
                                record_id: exportPayload.core_state.record_id,
                                jurisdiction: exportPayload.core_state.jurisdiction,
                                system_primary: exportPayload.core_state.system_primary,
                                evidence: exportPayload.source_facts.evidence_items,
                                findings: exportPayload.source_facts.finding_items,
                                statutes: exportPayload.core_state.statutes,
                              };
                              const targetOrg = (actionBundle.endpoints ?? []).find(ep => ep.entity_type === "nonprofit_advocacy" || ep.entity_type === "legal_aid_foundation");
                              if (targetOrg) {
                                const html = generateEscalationLetter(caseData as any, targetOrg.entity_name, targetOrg.entity_type as any);
                                openDocumentForPrint(html, `Escalation-Letter-${exportPayload.core_state.record_id}.html`);
                              }
                            }}>
                              <Mail className="h-3.5 w-3.5 mr-1.5" /> Generate Escalation Letter
                            </Button>
                          )}
                        </div>
                      )}
                    </Card>

                    {/* Action Items */}
                    <Card className="p-4 bg-card border-border/50">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
                        Action Items ({(actionBundle.actions ?? []).length})
                      </h4>
                      <div className="space-y-2 max-h-[600px] overflow-y-auto">
                        {(actionBundle.actions ?? []).map((action) => (
                          <ActionItemCard key={action.id} action={action} bundle={actionBundle} />
                        ))}
                      </div>
                    </Card>

                    {/* Escalation Matrix */}
                    <Card className="p-4 bg-card border-border/50">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
                        Escalation Matrix
                      </h4>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Levels</p>
                          <p className="text-lg font-bold text-foreground mt-1">{actionBundle.escalation_matrix?.levels ?? 0}</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Max Timeline</p>
                          <p className="text-lg font-bold text-foreground mt-1">{actionBundle.escalation_matrix?.max_escalation_timeline_days ?? 0}d</p>
                        </div>
                        <div className="p-3 rounded-lg bg-muted/20 border border-border/20">
                          <p className="text-[10px] font-mono text-muted-foreground uppercase">Triggers</p>
                          <p className="text-lg font-bold text-foreground mt-1">{actionBundle.escalation_matrix?.triggers?.length ?? 0}</p>
                        </div>
                      </div>
                      {(actionBundle.escalation_matrix?.triggers?.length ?? 0) > 0 && (
                        <div className="mt-3 flex flex-wrap gap-1">
                          {actionBundle.escalation_matrix.triggers.map((t, i) => (
                            <code key={i} className="text-[10px] font-mono text-muted-foreground bg-muted/20 px-1.5 py-0.5 rounded">{t}</code>
                          ))}
                        </div>
                      )}
                    </Card>

                    {/* Bundle Integrity */}
                    <Card className="p-4 bg-card border-border/50">
                      <h4 className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-3">
                        Bundle Integrity
                      </h4>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-muted-foreground">Bundle Hash:</span>
                          <code className="text-xs font-mono text-foreground">{truncateHash(actionBundle.bundle_hash, 20)}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-muted-foreground">Source Export Hash:</span>
                          <code className="text-xs font-mono text-foreground">{truncateHash(actionBundle.source_export_hash, 20)}</code>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-muted-foreground">Export ID:</span>
                          <code className="text-xs font-mono text-foreground">{truncateHash(actionBundle.export_id, 20)}</code>
                        </div>
                      </div>
                    </Card>
                  </>
                )}
              </div>
            )}

            {/* ═══════════════════════════════════════════════════════════════
                APPROVAL QUEUE TAB (admin only)
            ═══════════════════════════════════════════════════════════════ */}
            {isAdmin && activeTab === "approval" && (
              <div className="space-y-4">
                <Card className="p-4 bg-card border-border/50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-primary" />
                      Approval Queue
                    </h3>
                    <Button variant="outline" size="sm" onClick={() => refetchQueue()}>
                      <Activity className="h-3.5 w-3.5 mr-1.5" /> Refresh
                    </Button>
                  </div>

                  {(!approvalQueueList || approvalQueueList.length === 0) && (
                    <div className="text-center py-8">
                      <ListChecks className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                      <p className="text-sm text-muted-foreground">No cases in the approval queue.</p>
                      <p className="text-xs text-muted-foreground mt-1">Cases enter the queue when a user clicks "Prepare Action" on a ready case.</p>
                    </div>
                  )}

                  {approvalQueueList && approvalQueueList.length > 0 && (
                    <div className="space-y-3">
                      {approvalQueueList.map((entry) => (
                        <div key={entry.case_id} className="border border-border/30 rounded-lg p-4 space-y-3">
                          {/* Entry Header */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <code className="text-xs font-mono text-foreground">{truncateHash(entry.case_id, 16)}</code>
                              <Badge className={`text-[10px] ${STATE_COLORS[entry.state as ApprovalState] ?? STATE_COLORS.not_ready}`}>
                                {STATE_LABELS[entry.state as ApprovalState] ?? entry.state}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-[10px] font-mono text-muted-foreground">
                              <span>{entry.action_count} actions</span>
                              <span>{entry.endpoint_count} endpoints</span>
                            </div>
                          </div>

                          {/* Timestamps */}
                          <div className="grid grid-cols-5 gap-2">
                            <div className="p-1.5 rounded bg-muted/10 border border-border/20">
                              <p className="text-[9px] font-mono text-muted-foreground uppercase">Prepared</p>
                              <p className="text-[10px] text-foreground">{entry.prepared_at ? formatTimestamp(entry.prepared_at) : "—"}</p>
                            </div>
                            <div className="p-1.5 rounded bg-muted/10 border border-border/20">
                              <p className="text-[9px] font-mono text-muted-foreground uppercase">Approved</p>
                              <p className="text-[10px] text-foreground">{entry.approved_at ? formatTimestamp(entry.approved_at) : "—"}</p>
                            </div>
                            <div className="p-1.5 rounded bg-muted/10 border border-border/20">
                              <p className="text-[9px] font-mono text-muted-foreground uppercase">Sent</p>
                              <p className="text-[10px] text-foreground">{entry.sent_at ? formatTimestamp(entry.sent_at) : "—"}</p>
                            </div>
                            <div className="p-1.5 rounded bg-muted/10 border border-border/20">
                              <p className="text-[9px] font-mono text-muted-foreground uppercase">Acknowledged</p>
                              <p className="text-[10px] text-foreground">{entry.acknowledged_at ? formatTimestamp(entry.acknowledged_at) : "—"}</p>
                            </div>
                            <div className="p-1.5 rounded bg-muted/10 border border-border/20">
                              <p className="text-[9px] font-mono text-muted-foreground uppercase">Completed</p>
                              <p className="text-[10px] text-foreground">{entry.completed_at ? formatTimestamp(entry.completed_at) : "—"}</p>
                            </div>
                          </div>

                          {/* Guard Failures */}
                          {entry.guard_result && !entry.guard_result.passed && (
                            <div className="p-2 rounded bg-red-500/5 border border-red-500/20">
                              <p className="text-[10px] font-mono text-red-400 uppercase mb-1 flex items-center gap-1">
                                <Ban className="h-3 w-3" /> Transmission Guards Failed
                              </p>
                              <ul className="space-y-0.5">
                                {entry.guard_result.failures.map((f, i) => (
                                  <li key={i} className="text-[10px] text-red-300 flex items-start gap-1">
                                    <XCircle className="h-3 w-3 mt-0.5 shrink-0" /> {f}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {/* Action Buttons */}
                          <div className="flex items-center gap-2 pt-1">
                            {entry.state === "prepared" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleApprove(entry.case_id)}
                                disabled={approveMutation.isPending}
                              >
                                {approveMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                                Approve
                              </Button>
                            )}
                            {entry.state === "approved" && (
                              <Button
                                size="sm"
                                onClick={() => handleQueue(entry.case_id)}
                                disabled={queueMutation.isPending || (entry.guard_result !== null && !entry.guard_result.passed)}
                                className="bg-amber-600 text-white hover:bg-amber-700"
                              >
                                {queueMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Clock className="h-3.5 w-3.5 mr-1.5" />}
                                Queue for Transmission
                              </Button>
                            )}
                            {entry.state === "queued" && (
                              <Button
                                size="sm"
                                onClick={() => handleSend(entry.case_id)}
                                disabled={sendMutation.isPending}
                                className="bg-primary text-primary-foreground"
                              >
                                {sendMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
                                Send
                              </Button>
                            )}
                            {entry.state === "sent" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleAcknowledge(entry.case_id)}
                                disabled={acknowledgeMutation.isPending}
                              >
                                {acknowledgeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 mr-1.5" />}
                                Acknowledge
                              </Button>
                            )}
                            {entry.state === "acknowledged" && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleComplete(entry.case_id)}
                                disabled={completeMutation.isPending}
                              >
                                {completeMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                                Complete
                              </Button>
                            )}
                            {entry.state === "completed" && (
                              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
                                <CheckCircle2 className="h-3 w-3 mr-1" /> Transmission Cycle Complete
                              </Badge>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
